import React, { useState, useMemo, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { formatCurrency } from '../../../utils/currency';
import TransactionForm from './TransactionForm';

const TransactionLedger = ({ transactions, currency, onTransactionUpdate, onTransactionDelete, onTransactionAdd, accountBalance, account, preFillData, onPreFillDataUsed }) => {
  const [sortBy, setSortBy] = useState('date-desc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const itemsPerPage = 10;

  // Auto-open form when preFillData is provided
  useEffect(() => {
    if (preFillData) {
      setShowAddForm(true);
    }
  }, [preFillData]);

  const sortedAndFilteredTransactions = useMemo(() => {
    let filtered = [...transactions];

    if (searchTerm) {
      filtered = filtered?.filter(t => 
        t?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        t?.category?.toLowerCase()?.includes(searchTerm?.toLowerCase())
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered?.filter(t => 
        filterStatus === 'cleared' ? t?.cleared : !t?.cleared
      );
    }

    filtered?.sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return new Date(b?.date) - new Date(a?.date);
        case 'date-asc':
          return new Date(a?.date) - new Date(b?.date);
        case 'amount-desc':
          return Math.abs(b?.amount) - Math.abs(a?.amount);
        case 'amount-asc':
          return Math.abs(a?.amount) - Math.abs(b?.amount);
        default:
          return 0;
      }
    });

    return filtered;
  }, [transactions, sortBy, filterStatus, searchTerm]);

  const calculateRunningBalance = () => {
    if (!transactions?.length) return [];
    
    const isLiability = account?.group === 'Liabilities';
    let runningBalance = account?.balance || 0;
    
    // Sort transactions by date (newest first)
    const sortedTransactions = [...transactions]?.sort((a, b) => 
      new Date(b?.date) - new Date(a?.date)
    );
    
    // Calculate running balance backwards from current balance
    const withBalances = sortedTransactions?.map((txn, index) => {
      const txnWithBalance = { ...txn, runningBalance };
      
      // For liability accounts, the logic is:
      // - Charges (positive amounts) increased the debt
      // - Payments (negative amounts) decreased the debt
      // Going backwards: subtract the transaction amount
      runningBalance = runningBalance - txn?.amount;
      
      return txnWithBalance;
    });
    
    return withBalances;
  };

  const transactionsWithBalance = calculateRunningBalance();

  const totalPages = Math.ceil(transactionsWithBalance?.length / itemsPerPage);
  const paginatedTransactions = transactionsWithBalance?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleEdit = (transaction) => {
    setEditingTransaction(transaction);
    setShowAddForm(true);
  };

  const handleDelete = (transactionId) => {
    onTransactionDelete(transactionId);
  };

  const handleFormSubmit = (transactionData) => {
    if (editingTransaction) {
      onTransactionUpdate({ ...editingTransaction, ...transactionData });
    } else {
      onTransactionAdd({
        id: `t${Date.now()}`,
        ...transactionData
      });
    }
    setShowAddForm(false);
    setEditingTransaction(null);
  };

  const handleFormCancel = () => {
    setShowAddForm(false);
    setEditingTransaction(null);
  };

  const handleToggleCleared = (transactionId, cleared) => {
    const transaction = transactions?.find(t => t?.id === transactionId);
    if (transaction) {
      onTransactionUpdate({
        ...transaction,
        cleared
      });
    }
  };

  const sortOptions = [
    { value: 'date-desc', label: 'Date (Newest)' },
    { value: 'date-asc', label: 'Date (Oldest)' },
    { value: 'amount-desc', label: 'Amount (High to Low)' },
    { value: 'amount-asc', label: 'Amount (Low to High)' }
  ];

  const filterOptions = [
    { value: 'all', label: 'All Transactions' },
    { value: 'cleared', label: 'Cleared Only' },
    { value: 'uncleared', label: 'Uncleared Only' }
  ];

  return (
    <div className="bg-card rounded-lg shadow-elevation-2">
      <div className="p-20 md:p-24 border-b border-border">
        <div className="flex items-center justify-between mb-20">
          <h3 className="text-lg md:text-xl font-heading font-semibold text-card-foreground">
            Transaction Ledger
          </h3>
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            iconSize={16}
            onClick={() => setShowAddForm(!showAddForm)}
          >
            Add Transaction
          </Button>
        </div>

        {showAddForm && (
          <div className="mb-20 p-16 bg-muted rounded-lg">
            <TransactionForm
              transaction={editingTransaction}
              account={account}
              onSubmit={handleFormSubmit}
              onCancel={handleFormCancel}
              preFillData={preFillData}
              onPreFillDataUsed={onPreFillDataUsed}
            />
          </div>
        )}

        <div className="grid grid-cols-1 gap-12">
          <Input
            type="text"
            placeholder="Search transactions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e?.target?.value)}
            className="w-full"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-12">
            <Select
              options={sortOptions}
              value={sortBy}
              onChange={setSortBy}
              placeholder="Sort by"
            />
            <Select
              options={filterOptions}
              value={filterStatus}
              onChange={setFilterStatus}
              placeholder="Filter"
            />
          </div>
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-16 py-12 text-left text-xs font-caption font-semibold text-muted-foreground uppercase tracking-wider">
                Date
              </th>
              <th className="px-16 py-12 text-left text-xs font-caption font-semibold text-muted-foreground uppercase tracking-wider">
                Description
              </th>
              <th className="px-16 py-12 text-right text-xs font-caption font-semibold text-muted-foreground uppercase tracking-wider">
                Amount
              </th>
              <th className="px-16 py-12 text-right text-xs font-caption font-semibold text-muted-foreground uppercase tracking-wider">
                Balance
              </th>
              <th className="px-16 py-12 text-center text-xs font-caption font-semibold text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {paginatedTransactions?.map((txn) => {
              const isLiability = account?.group === 'Liabilities';
              const isCharge = isLiability && txn?.amount > 0;
              const isPayment = isLiability && txn?.amount < 0;
              
              return (
                <tr key={txn?.id} className="hover:bg-muted/50 transition-colors">
                  <td className="px-16 py-12">
                    <div className="flex items-center gap-8">
                      <Checkbox
                        checked={txn?.cleared}
                        onChange={(e) => handleToggleCleared(txn?.id, e?.target?.checked)}
                      />
                      <span className="text-sm text-foreground">
                        {new Date(txn?.date)?.toLocaleDateString()}
                      </span>
                    </div>
                  </td>
                  <td className="px-16 py-12">
                    <div className="flex items-start gap-8">
                      <Icon 
                        name={txn?.categoryIcon || 'Tag'} 
                        size={16} 
                        style={{ color: txn?.categoryColor || '#6366f1' }}
                        className="mt-2"
                      />
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {txn?.description}
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {txn?.category}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="px-16 py-12 text-right">
                    <span className={`text-sm font-semibold ${
                      isLiability
                        ? (isCharge ? 'text-error' : 'text-success')
                        : (txn?.amount >= 0 ? 'text-success' : 'text-error')
                    }`}>
                      {isLiability
                        ? (isCharge ? '+' : '-')
                        : (txn?.amount >= 0 ? '+' : '-')}
                      {formatCurrency(txn?.amount)}
                    </span>
                  </td>
                  <td className="px-16 py-12 text-right">
                    <span className="text-sm font-medium text-foreground">
                      {formatCurrency(txn?.runningBalance)}
                    </span>
                  </td>
                  <td className="px-16 py-12">
                    <div className="flex items-center justify-center gap-8">
                      <button
                        onClick={() => handleEdit(txn)}
                        className="p-6 hover:bg-muted rounded transition-colors"
                        aria-label="Edit transaction"
                      >
                        <Icon name="Edit" size={16} color="var(--color-primary)" />
                      </button>
                      <button
                        onClick={() => handleDelete(txn?.id)}
                        className="p-6 hover:bg-muted rounded transition-colors"
                        aria-label="Delete transaction"
                      >
                        <Icon name="Trash2" size={16} color="var(--color-error)" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden divide-y divide-border">
        {paginatedTransactions?.map((txn) => {
          const isLiability = account?.group === 'Liabilities';
          const isCharge = isLiability && txn?.amount > 0;
          const isPayment = isLiability && txn?.amount < 0;
          
          return (
            <div key={txn?.id} className="p-16 hover:bg-muted/50 transition-colors">
              {/* Date and Checkbox Row */}
              <div className="flex items-center justify-between mb-12">
                <div className="flex items-center gap-8">
                  <Checkbox
                    checked={txn?.cleared}
                    onChange={(e) => handleToggleCleared(txn?.id, e?.target?.checked)}
                  />
                  <span className="text-sm font-medium text-foreground">
                    {new Date(txn?.date)?.toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-8">
                  <button
                    onClick={() => handleEdit(txn)}
                    className="p-6 hover:bg-muted rounded transition-colors"
                    aria-label="Edit transaction"
                  >
                    <Icon name="Edit" size={16} color="var(--color-primary)" />
                  </button>
                  <button
                    onClick={() => handleDelete(txn?.id)}
                    className="p-6 hover:bg-muted rounded transition-colors"
                    aria-label="Delete transaction"
                  >
                    <Icon name="Trash2" size={16} color="var(--color-error)" />
                  </button>
                </div>
              </div>

              {/* Description and Category */}
              <div className="flex items-start gap-8 mb-12">
                <Icon 
                  name={txn?.categoryIcon || 'Tag'} 
                  size={18} 
                  style={{ color: txn?.categoryColor || '#6366f1' }}
                  className="mt-2 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-base font-medium text-foreground break-words">
                    {txn?.description}
                  </p>
                  <p className="text-sm text-muted-foreground mt-2">
                    {txn?.category}
                  </p>
                </div>
              </div>

              {/* Amount and Balance Row */}
              <div className="flex items-center justify-between pt-12 border-t border-border">
                <div>
                  <p className="text-xs text-muted-foreground mb-2">Amount</p>
                  <span className={`text-base font-semibold ${
                    isLiability
                      ? (isCharge ? 'text-error' : 'text-success')
                      : (txn?.amount >= 0 ? 'text-success' : 'text-error')
                  }`}>
                    {isLiability
                      ? (isCharge ? '+' : '-')
                      : (txn?.amount >= 0 ? '+' : '-')}
                    {formatCurrency(txn?.amount)}
                  </span>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground mb-2">Balance</p>
                  <span className="text-base font-semibold text-foreground">
                    {formatCurrency(txn?.runningBalance)}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-20 md:px-24 py-16 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-12">
        <p className="text-sm text-muted-foreground">
          Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, transactionsWithBalance?.length)} of {transactionsWithBalance?.length} transactions
        </p>
        <div className="flex items-center gap-8">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </Button>
          <span className="text-sm font-medium text-foreground px-12">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TransactionLedger;